import React, { Suspense } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '@shared/contexts/AuthContext';
import { api } from '@shared/services/api';
import { UserInfo } from '@shared/types';
import { trackEvent } from '@/lib/analytics';

const clientId = '270682995926-36p33ghao9bqivffuv3vj884spjei8no.apps.googleusercontent.com';

const GoogleLoginComponent = React.lazy(() => import('@/components/GoogleLoginComponent'));

export default function LoginPage() {
  const { login } = useAuth();

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const info: any = jwtDecode(credentialResponse.credential);
      const userPayload: UserInfo = {
        name: info.name,
        email: info.email,
        avatar: info.picture,
        google_id: info.sub,
      };
      const response = await api.googleAuth(userPayload);
      if (response?.data?.token) {
        // Conversion event — fires on every Google sign-in (GA4 recommended name).
        trackEvent('sign_up', { method: 'google' });
        login(response.data.token);
      } else {
        throw new Error('No token returned from backend');
      }
    } catch (error) {
      console.error('Login failed:', error);
      alert('Login failed. Please try again.');
    }
  };

  return (
    <div className="bg-gradient-to-b from-orange-400 via-orange-100 to-white min-h-screen flex flex-row">
      {/* Left: Feature showcase */}
      <div className="hidden lg:flex flex-[6_0_0%] items-center justify-center p-12">
        <div className="max-w-xl text-center">
          <h2 className="text-4xl font-bold mb-4 text-gray-800">Your notes, everywhere.</h2>
          <p className="text-lg text-gray-600 mb-8">
            Write, organize, and collaborate on notes — from your browser tab to your full workspace.
          </p>
          <div className="grid grid-cols-2 gap-4 text-left">
            {[
              { icon: '📝', title: 'Rich Text Editor', desc: 'Format with headings, lists, tasks, and more' },
              { icon: '🤝', title: 'Collaborate', desc: 'Share notes with teammates or your whole org' },
              { icon: '💬', title: 'Comments', desc: 'Discuss ideas with threaded comments' },
              { icon: '🔗', title: 'Share Links', desc: 'Generate public links with expiry control' },
            ].map(f => (
              <div key={f.title} className="bg-white/60 rounded-xl p-4 shadow-sm">
                <div className="text-2xl mb-2">{f.icon}</div>
                <div className="font-semibold text-gray-800 text-sm">{f.title}</div>
                <div className="text-xs text-gray-500 mt-1">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Sign in */}
      <div className="flex-1 lg:flex-[4_0_0%] flex flex-col justify-center items-center p-8 bg-gradient-to-br from-orange-50 to-purple-50 dark:from-background dark:to-card">
        <img src="/icons/icon.svg" alt="Hintword" className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-foreground">Sign in to Hintword</h1>
        <p className="mb-8 text-muted-foreground">Your tabs, notes, and focus — everywhere.</p>

        <Suspense fallback={<div className="text-muted-foreground text-sm">Loading...</div>}>
          <GoogleLoginComponent onSuccess={handleGoogleSuccess} clientId={clientId} />
        </Suspense>

        <p className="mt-10 text-xs text-muted-foreground text-center">
          By signing in, you agree to our{' '}
          <a href="/terms" className="underline hover:text-foreground">Terms</a>{' '}
          and{' '}
          <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
